/**
 * Gemini Service - Wrapper for AI generation (now routes through User Backend)
 * 
 * This service maintains backward compatibility with existing components
 * while routing all AI requests through the secure User Backend.
 */

import * as aiService from './aiService';
import { fetchImageViaProxy } from './backendService';

// Helper to convert CDN URL to base64 immediately after generation
// This prevents CORS issues with expiring fal.media URLs
async function convertToBase64IfNeeded(imageUrl) {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('data:')) return imageUrl; // Already base64
    
    // Convert CDN URLs to base64 immediately
    if (imageUrl.startsWith('http')) {
        try {
            console.log('🔄 Converting generated image to base64:', imageUrl.substring(0, 60) + '...');
            const base64 = await fetchImageViaProxy(imageUrl);
            if (base64 && base64.startsWith('data:')) {
                console.log('✅ Successfully converted to base64');
                return base64;
            }
        } catch (e) {
            console.warn('⚠️ Failed to convert to base64, keeping URL:', e.message);
        }
    }
    
    return imageUrl;
}

// ==================== SCRIPT GENERATION ====================

export const generateScriptFromAudio = async (audioData, style = 'dialogue', region = 'Global', language = 'English') => {
    // For now, route through the AI service or handle locally
    // This requires audio transcription + script generation
    try {
        const transcription = await aiService.transcribeAudio(audioData);
        
        // Validate transcription is a string
        if (!transcription || typeof transcription !== 'string' || !transcription.trim()) {
            throw new Error('Could not transcribe audio. Please try again or use a different audio file.');
        }
        
        console.log('🎤 Transcription text (first 100 chars):', transcription.substring(0, 100));
        
        return await aiService.generateScript(transcription, style, region, language);
    } catch (e) {
        console.error('Script from audio generation failed:', e);
        throw e;
    }
};

export const generateScriptFromConcept = async (concept, style, region, language, projectDetails = {}) => {
    return aiService.generateScript(concept, style, region, language, projectDetails);
};

export const generateConceptsFromProduct = async (concept, region, projectDetails = {}) => {
    return aiService.generateConcepts(concept, region, projectDetails);
};

export const generateScriptFromVideo = async (videoInput, instructions, style = 'dialogue', region = 'Global', language = 'English') => {
    // This would require video analysis - placeholder for now
    const prompt = instructions?.text || 'Create a script based on this video';
    return aiService.generateScript(prompt, style, region, language);
};

// ==================== CHARACTER GENERATION ====================

export const generateCharacters = async (script) => {
    return aiService.generateCharacters(script);
};

// ==================== STORYBOARD GENERATION ====================

export const generateStoryboard = async (project) => {
    return aiService.generateStoryboard(project);
};

export const mapEntitiesToShot = (shotData, characters) => {
    const text = (shotData.description + (shotData.dialogue ? JSON.stringify(shotData.dialogue) : '')).toLowerCase();
    const characterIds = characters.filter(c => c.category !== 'element' && text.includes(c.name.toLowerCase())).map(c => c.id);
    const elementIds = characters.filter(c => c.category === 'element' && text.includes(c.name.toLowerCase())).map(c => c.id);
    return { characterIds, elementIds };
};

// ==================== IMAGE GENERATION ====================

export const generateCharacterPortraitWithGemini = async (description, refImages, costumeImages, makeup, aspectRatio, category, modelId) => {
    const result = await aiService.generateCharacterPortrait(description, refImages, costumeImages, makeup, aspectRatio, category, modelId);
    // Return the image, converted to base64 to avoid CORS issues with CDN URLs
    if (result.images && result.images.length > 0) {
        const image = await convertToBase64IfNeeded(result.images[0]);
        return {
            image,
            creditsUsed: result.creditsUsed || 0,
            creditsRemaining: result.creditsRemaining,
            jobId: result.jobId
        };
    }
    throw new Error('No image generated');
};

export const generateSceneImage = async (prompt, aspectRatio, characters = [], referenceImages = [], modelId = 'default') => {
    const result = await aiService.generateSceneImage(prompt, aspectRatio, characters, referenceImages, modelId);
    console.log('🎨 aiService.generateSceneImage result:', {
        hasImages: !!(result.images && result.images.length > 0),
        creditsUsed: result.creditsUsed,
        creditsRemaining: result.creditsRemaining,
        jobId: result.jobId
    });
    // Return the image, converted to base64 to avoid CORS issues with CDN URLs
    if (result.images && result.images.length > 0) {
        const image = await convertToBase64IfNeeded(result.images[0]);
        return {
            image,
            creditsUsed: result.creditsUsed || 0,
            creditsRemaining: result.creditsRemaining,
            jobId: result.jobId
        };
    }
    throw new Error('No image generated');
};

export const editImage = async (baseImage, prompt, characters = [], referenceImages = [], maskImage = null, modelId = 'default') => {
    const result = await aiService.editImage(baseImage, prompt, characters, referenceImages, maskImage, modelId);
    // Convert to base64 to avoid CORS issues
    const imageUrl = await convertToBase64IfNeeded(result.imageUrl);
    return {
        imageUrl,
        creditsUsed: result.creditsUsed || 0,
        creditsRemaining: result.creditsRemaining,
        jobId: result.jobId
    };
};

// ==================== VIDEO GENERATION ====================

export const generateVideoWithWanAnimate = async (imageUrl, referenceVideoData, onProgress, resolution = '480p') => {
    const result = await aiService.generateVideoWithWan(
        "Animate this image with natural, fluid motion",
        imageUrl,
        '16:9',
        resolution,
        onProgress
    );
    return result.videoUrl;
};

export const generateSceneVideoWithVeoFast = async (prompt, onProgress, aspectRatio, startFrame, endFrame) => {
    // Veo is not available in the new architecture, use default video generation
    const result = await aiService.generateVideo(prompt, 5, 'cinematic', aspectRatio, startFrame, endFrame, onProgress);
    return result.videoUrl;
};

export const generateSceneVideoWithSeedance = async (prompt, onProgress, aspectRatio, startFrame, endFrame) => {
    const result = await aiService.generateVideoWithMinimax(prompt, startFrame, onProgress);
    return result.videoUrl;
};

// ==================== PROMPT GENERATION ====================

export const generateFramePromptsFromDescription = async (description) => {
    return aiService.generateFramePrompts(description);
};

export const generateVideoPrompt = async (shot, characters, script, duration) => {
    return aiService.generateVideoPrompt(shot, characters, script, duration);
};

// ==================== TRANSCRIPTION ====================

export const transcribeDialogueAudio = async (audioData) => {
    return aiService.transcribeAudio(audioData);
};

export const transcribeShotNarration = transcribeDialogueAudio;

// ==================== UTILITY FUNCTIONS ====================

export const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

export const cropImageToAspectRatio = async (base64Str, aspectRatio) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const [wRatio, hRatio] = aspectRatio.split(':').map(Number);
            const targetRatio = wRatio / hRatio;
            const imgRatio = img.width / img.height;
            let renderWidth = img.width;
            let renderHeight = img.height;
            let offsetX = 0;
            let offsetY = 0;

            if (imgRatio > targetRatio) {
                renderWidth = img.height * targetRatio;
                offsetX = (img.width - renderWidth) / 2;
            } else {
                renderHeight = img.width / targetRatio;
                offsetY = (img.height - renderHeight) / 2;
            }
            canvas.width = renderWidth;
            canvas.height = renderHeight;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, offsetX, offsetY, renderWidth, renderHeight, 0, 0, renderWidth, renderHeight);
            resolve(canvas.toDataURL('image/jpeg', 0.9));
        };
        img.onerror = () => resolve(base64Str);
        img.src = base64Str;
    });
};

export const reframeImage = async (img, ratio) => cropImageToAspectRatio(img, ratio);

export const preloadProjectAssets = async (project, onProgress) => {
    // In the new architecture, assets are managed server-side
    // This function now just validates that assets exist
    const assetsToLoad = new Set();
    project.characters?.forEach(c => {
        if (c.image) assetsToLoad.add(c.image);
    });
    project.scenes?.forEach(s => s.shots?.forEach(shot => {
        if (shot.startFrameImage) assetsToLoad.add(shot.startFrameImage);
        if (shot.endFrameImage) assetsToLoad.add(shot.endFrameImage);
    }));
    
    const total = assetsToLoad.size;
    if (total === 0) return;
    if (onProgress) onProgress(`Found ${total} assets to preload`);
    
    // Assets are now URL-based and served from the backend
    // No need to upload to Gemini Files API
    if (onProgress) onProgress(`Assets ready (${total} items)`);
};

// Portrait generation aliases
export const generateCharacterPortraitWithFal = async (prompt, ratio) => {
    return generateCharacterPortraitWithGemini(prompt, [], [], '', ratio, 'character', 'default');
};

export const generateCharacterPortraitWithFalRef = async (prompt, refs, costume, makeup, ratio) => {
    return generateCharacterPortraitWithGemini(prompt, refs, costume, makeup, ratio, 'character', 'default');
};

// Video resize utility (client-side)
export const resizeVideo = async (videoUrl, maxDimension = 1920) => {
    // Return as-is for URLs, the server handles video processing
    if (videoUrl.startsWith('http')) {
        return videoUrl;
    }
    
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.src = videoUrl;
        video.muted = true;
        
        const timeout = setTimeout(() => {
             console.warn("Resize video timeout, returning original.");
             resolve(videoUrl);
        }, 15000);

        video.onloadedmetadata = () => {
            clearTimeout(timeout);
            const { videoWidth, videoHeight } = video;
            
            if (videoWidth <= maxDimension && videoHeight <= maxDimension) {
                resolve(videoUrl);
                return;
            }
            
            // For large videos, just return the URL - server will handle processing
            resolve(videoUrl);
        };
        
        video.onerror = () => {
            clearTimeout(timeout);
            resolve(videoUrl);
        };
    });
};

/**
 * Generate multiple ad images using the batch endpoint.
 * Wraps aiService.generateAdImages and converts URLs to base64 for display (to avoid CDN issues).
 * Returns both base64 for display and original URLs for saving.
 */
export const generateAdImages = async (params, onProgress) => {
    const result = await aiService.generateAdImages(params, onProgress);
    
    // Convert fal.media URLs to base64 to avoid QUIC protocol errors for display
    if (result.success && result.images && result.images.length > 0) {
        if (onProgress) onProgress({ status: 'processing', message: 'Processing generated images...' });
        
        // Keep original URLs for saving to backend
        const originalUrls = [...result.images];
        
        const convertedImages = await Promise.all(
            result.images.map(async (imageUrl, index) => {
                try {
                    // Skip if already base64
                    if (imageUrl.startsWith('data:')) return imageUrl;
                    
                    console.log(`🔄 Converting image ${index + 1} to base64 for display...`);
                    
                    // Try proxy first, then direct fetch
                    let base64 = await fetchImageViaProxy(imageUrl);
                    
                    if (!base64 || !base64.startsWith('data:')) {
                        // Fallback: try direct fetch with no-cors workaround
                        console.log(`🔄 Proxy failed, trying direct fetch for image ${index + 1}...`);
                        base64 = await directFetchToBase64(imageUrl);
                    }
                    
                    if (base64 && base64.startsWith('data:')) {
                        console.log(`✅ Image ${index + 1} converted successfully`);
                        return base64;
                    }
                    
                    // Fallback to original URL if all conversion methods fail
                    console.warn(`⚠️ Image ${index + 1} conversion failed, using original URL`);
                    return imageUrl;
                } catch (e) {
                    console.warn(`⚠️ Failed to convert image ${index + 1}:`, e.message);
                    return imageUrl;
                }
            })
        );
        
        // Return both: converted images for display, original URLs for saving
        return { 
            ...result, 
            images: convertedImages,
            originalUrls: originalUrls  // Keep original URLs for backend save
        };
    }
    
    return result;
};

// Helper function to fetch image directly and convert to base64
async function directFetchToBase64(imageUrl) {
    try {
        const response = await fetch(imageUrl, { mode: 'cors' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        console.warn('Direct fetch failed:', e.message);
        return null;
    }
}

